/**
 * Moteur de cotation Corridor FX.
 *
 * Module **pur** (AD-3) : entrées vers sortie, sans effet de bord, sans DOM,
 * sans réseau. Aucune règle de calcul ne doit exister ailleurs - un composant
 * qui a besoin d'un chiffre appelle ce module.
 *
 * La source de vérité est `03-moteur-de-calcul.md`. En cas de divergence entre
 * ce code et ce document, c'est le document qui a raison. Les cas CT-1 à CT-7
 * sont couverts par `moteur.test.js`, et confrontés à l'oracle Python de
 * référence.
 *
 * Règle d'arrondi : pleine précision flottante, aucun arrondi intermédiaire,
 * à la seule exception du plancher par tranche de frais opérateur, qui est une
 * règle métier et non un arrondi d'affichage. Ne jamais réintroduire dans un
 * calcul un montant déjà arrondi pour l'affichage.
 */

/** Parité officielle par défaut. XOF et XAF la partagent. */
export const PARITE_OFFICIELLE = 655.957;

/**
 * Garde-fou de découpe. L'opérateur plafonne le montant par transfert : une
 * somme énorme face à un plafond minuscule produirait une boucle sans fin.
 */
const TRANCHES_MAX = 200;

/** Itérations de résolution de la circularité du mode « déduit ». */
const ITERATIONS_DEDUIT = 5;

/**
 * Frais opérateur d'un versement.
 *
 * L'opérateur facture chaque transfert séparément et plafonne le montant
 * unitaire : on découpe la somme en tranches, puis on additionne les frais
 * tranche par tranche. Le plafond s'applique **par transfert**, jamais au
 * total.
 *
 * Le plancher à l'unité CFA sur chaque tranche n'est pas cosmétique : c'est lui
 * qui reproduit les débits réellement observés (CT-5 et CT-6).
 *
 * @param {number} montantCfa
 * @param {number} pct     pourcentage, ex. 0.2 pour 0,2 %
 * @param {number} fixe    part fixe en CFA
 * @param {number} plafond montant maximal par transfert, 0 = pas de plafond
 * @returns {{n: number, total: number, tranches: number[]}}
 */
export function fraisOperateur(montantCfa, pct, fixe, plafond) {
  if (montantCfa <= 0 || (pct === 0 && fixe === 0)) {
    return { n: 0, total: 0, tranches: [] };
  }

  const decoupe = [];
  let reste = montantCfa;
  if (plafond > 0) {
    while (reste > plafond && decoupe.length < TRANCHES_MAX) {
      decoupe.push(plafond);
      reste -= plafond;
    }
  }
  decoupe.push(reste);

  const frais = decoupe.map((t) => Math.floor((t * pct) / 100 + fixe));
  return {
    n: decoupe.length,
    total: frais.reduce((s, f) => s + f, 0),
    tranches: frais,
  };
}

/** Paramètres de profil, avec les valeurs par défaut du document 3. */
function lireProfil(p = {}) {
  return {
    officialRate: p.officialRate ?? PARITE_OFFICIELLE,
    rateOut: p.rateOut ?? PARITE_OFFICIELLE,
    rateIn: p.rateIn ?? PARITE_OFFICIELLE,
    serviceFixed: p.serviceFixed ?? 0,
    // Plafonné à 99 % : au-delà, k deviendrait nul ou négatif et la résolution
    // du montant en base « réception » n'aurait plus de sens.
    servicePercent: Math.min(99, p.servicePercent ?? 0),
    operatorPercent: p.operatorPercent ?? 0,
    operatorFixed: p.operatorFixed ?? 0,
    operatorCap: p.operatorCap ?? 0,
  };
}

/** Sens EUR vers CFA. */
function coterSortant(basis, feeMode, montantSaisi, profil) {
  const PAIR = profil.officialRate;
  const fixe = profil.serviceFixed;
  const pct = profil.servicePercent;
  const k = 1 - pct / 100;
  const tOut = profil.rateOut;
  const { operatorPercent: opP, operatorFixed: opF, operatorCap: cap } = profil;

  // Montant réellement envoyé par le client, en EUR.
  let montant;
  if (basis === 'envoi') {
    montant = montantSaisi;
  } else {
    // Le montant saisi est le net CFA voulu par le destinataire. En mode
    // « déduit », les frais opérateur sortent de l'enveloppe : il faut donc
    // pousser davantage pour que le destinataire touche bien la cible.
    const cible =
      montantSaisi +
      (feeMode === 'deduit' ? fraisOperateur(montantSaisi, opP, opF, cap).total : 0);
    montant = k > 0 ? (cible / tOut + fixe) / k : 0;
  }

  const fraisEur = Math.min(montant, fixe + (montant * pct) / 100);
  const netEur = Math.max(0, montant - fraisEur);
  const brutCfa = netEur * tOut;

  // Net réellement crédité. En mode « déduit » les frais se prennent dans
  // l'enveloppe, ce qui réduit le montant transféré, donc les frais eux-mêmes :
  // la circularité se résout par itération, la grille étant à paliers.
  let netCfa = brutCfa;
  if (feeMode === 'deduit') {
    for (let i = 0; i < ITERATIONS_DEDUIT; i += 1) {
      netCfa = brutCfa - fraisOperateur(netCfa, opP, opF, cap).total;
    }
    netCfa = Math.max(0, netCfa);
  }

  const op = fraisOperateur(netCfa, opP, opF, cap);
  const provision = netCfa + op.total;
  const opEurClient = tOut > 0 ? op.total / tOut : 0;

  const margeChange = netEur * (1 - tOut / PAIR);
  const coutOperateurPlateforme = feeMode === 'acharge' ? op.total / PAIR : 0;
  const margeNette = fraisEur + margeChange - coutOperateurPlateforme;

  let coutClient = montant - netCfa / PAIR;
  if (feeMode === 'refacture') coutClient += opEurClient;
  const totalRegleEur = montant + (feeMode === 'refacture' ? opEurClient : 0);

  return {
    direction: 'out',
    basis,
    feeMode,
    montantEnvoye: montant,
    fraisEur,
    brutCfa,
    opTotal: op.total,
    opTranches: op.tranches,
    opEurClient,
    netCfa,
    provision,
    totalRegleEur,
    margeChange,
    margeNette,
    coutClient,
    coutClientPct: montant > 0 ? (coutClient / montant) * 100 : 0,
    tauxEffectif: montant > 0 ? netCfa / montant : 0,
  };
}

/** Sens CFA vers EUR. */
function coterEntrant(basis, feeMode, montantSaisi, profil) {
  const PAIR = profil.officialRate;
  const fixe = profil.serviceFixed;
  const pct = profil.servicePercent;
  const k = 1 - pct / 100;
  const tIn = profil.rateIn;
  const { operatorPercent: opP, operatorFixed: opF, operatorCap: cap } = profil;

  // Montant collecté auprès du client, en CFA.
  const montant =
    basis === 'envoi'
      ? montantSaisi
      : k > 0
        ? (montantSaisi * tIn + fixe * PAIR) / k
        : 0;

  // Les frais de service sont contractés en EUR mais prélevés côté CFA : la
  // part fixe est convertie à la parité officielle, pas au taux appliqué, sans
  // quoi le spread se retrouverait compté deux fois.
  const fraisCfa = Math.min(montant, fixe * PAIR + (montant * pct) / 100);
  const netCfa = Math.max(0, montant - fraisCfa);
  const recuEur = tIn > 0 ? netCfa / tIn : 0;

  // Dans ce sens, le client règle les frais opérateur depuis son propre compte.
  // Ils ne sont donc qu'informatifs, sauf en mode « à charge » où ils pèsent
  // sur la marge.
  const op = fraisOperateur(montant, opP, opF, cap);

  const margeChange = netCfa / PAIR - netCfa / tIn;
  const coutOperateurPlateforme = feeMode === 'acharge' ? op.total / PAIR : 0;
  const margeNette = fraisCfa / PAIR + margeChange - coutOperateurPlateforme;

  return {
    direction: 'in',
    basis,
    feeMode,
    montantCollecte: montant,
    fraisCfa,
    netCfa,
    recuEur,
    opTotal: op.total,
    opTranches: op.tranches,
    margeChange,
    margeNette,
    tauxEffectif: recuEur > 0 ? montant / recuEur : 0,
  };
}

/**
 * Cote une transaction.
 *
 * @param {object} entrees
 * @param {'out'|'in'} entrees.direction
 * @param {'envoi'|'reception'} entrees.basis
 * @param {'refacture'|'deduit'|'acharge'} entrees.feeMode
 * @param {number} entrees.montant   montant saisi, dans l'unité dictée par basis
 * @param {object} entrees.profil
 */
export function coter({ direction, basis, feeMode, montant, profil }) {
  const p = lireProfil(profil);
  const saisi = Number.isFinite(montant) ? montant : 0;
  return direction === 'in'
    ? coterEntrant(basis, feeMode, saisi, p)
    : coterSortant(basis, feeMode, saisi, p);
}

/**
 * Le spread est-il inversé, c'est-à-dire le taux appliqué du mauvais côté de la
 * parité ? Le moteur calcule alors une marge négative sans rien bloquer : c'est
 * licite, et c'est à l'interface de le signaler.
 */
export function spreadInverse(direction, profil) {
  const p = lireProfil(profil);
  return direction === 'in' ? p.rateIn < p.officialRate : p.rateOut > p.officialRate;
}
